import React, { useEffect, useState } from "react";
import {
  User,
  Phone,
  ClipboardList,
  Package,
  CheckCircle,
  Clock,
  Edit,
  PlusCircle,
  FlaskConical,
  Download,
  X,
  Eye,
  AlertCircle,
  FileText,
  Activity,
  Beaker,
  Loader2,
} from "lucide-react";

import PathologyResultModal from "../modals/PathologyResultModal";
import { formatPatientAge } from "../../../../utils/referenceRange";
import RadiologyResultModal from "../modals/RadiologyResultModal";

const calculateDiscountedPrice = (price, discount) => {
  const p = Number(price) || 0;
  const d = Number(discount) || 0;
  if (!d) return p;
  return p - (p * d) / 100;
};

const StatusBadge = ({ status }) => {
  if (!status) {
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-600">
        PENDING
      </span>
    );
  }
  if (status === "APPROVED") {
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] bg-green-100 text-green-800">
        APPROVED
      </span>
    );
  }
  if (status === "DRAFT") {
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800">
        DRAFT
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-600">
      {status}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────────
   PDF CHOICE MODAL
───────────────────────────────────────────────────────────────── */
const PdfChoiceModal = ({ open, onClose, onPick, title = "Download Patient Report", options }) => {
  if (!open) return null;
  const defaultOptions = [
    { key: "plain",      title: "Plain",              desc: "Report only (no letterhead)" },
    { key: "letterhead", title: "With Letterhead",    desc: "Includes clinic header on pages" },
    { key: "full",       title: "Full (Cover + Last)",desc: "Cover page and last page included" },
  ];
  const choiceOptions = options || defaultOptions;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="mt-1 text-sm text-gray-500">Choose the format you want to export.</p>
          </div>
          <button onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50" type="button">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {choiceOptions.map((opt) => (
            <button key={opt.key} onClick={() => onPick(opt.key)}
              className="group w-full rounded-xl border border-gray-200 bg-white p-4 text-left hover:border-gray-300 hover:bg-gray-50" type="button">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{opt.title}</div>
                  <div className="mt-0.5 text-xs text-gray-500">{opt.desc}</div>
                </div>
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 group-hover:bg-gray-100">
                  <Download className="h-4 w-4" />
                </span>
              </div>
            </button>
          ))}
          <div className="mt-5 flex justify-end border-t border-gray-200 pt-4">
            <button onClick={onClose} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" type="button">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   FLAG BADGE (for parameter results)
───────────────────────────────────────────────────────────────── */
const FlagBadge = ({ flag }) => {
  if (!flag) return null;
  const f = flag.toUpperCase();
  const cls =
    f === "H" || f === "HIGH" || f === "CRITICAL HIGH"
      ? "bg-red-100 text-red-700 border-red-200"
      : f === "L" || f === "LOW" || f === "CRITICAL LOW"
      ? "bg-blue-100 text-blue-700 border-blue-200"
      : f === "N" || f === "NORMAL"
      ? "bg-green-100 text-green-700 border-green-200"
      : "bg-amber-100 text-amber-700 border-amber-200";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border ${cls}`}>
      {flag}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────────
   SIGNATURE CARD
───────────────────────────────────────────────────────────────── */
const SignatureCard = ({ sig, align }) => {
  if (!sig) return null;
  return (
    <div className={`flex flex-col items-center gap-1 ${
      align === "LEFT" ? "items-start" : align === "RIGHT" ? "items-end" : "items-center"
    }`}>
      {sig.signatureImg && (
        <img
          src={sig.signatureImg}
          alt={`${sig.name} signature`}
          className="h-10 object-contain"
        />
      )}
      <div className={`text-center ${align === "LEFT" ? "text-left" : align === "RIGHT" ? "text-right" : "text-center"}`}>
        <p className="text-xs font-semibold text-gray-800">{sig.name}</p>
        {sig.qualification && <p className="text-[10px] text-gray-500">{sig.qualification}</p>}
        {sig.designation && <p className="text-[10px] text-gray-400">{sig.designation}</p>}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   RESULT VIEW MODAL
───────────────────────────────────────────────────────────────── */
const ResultViewModal = ({ open, onClose, test, member, orderId }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  // itemId (string) → title, for FREE_TEXT / RICH_TEXT report items
  const [itemTitleMap, setItemTitleMap] = useState({});

  useEffect(() => {
    if (!open || !test) return;
    fetchResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, test]);

  const fetchResult = async () => {
    try {
      setLoading(true);
      setError(null);
      setResult(null);
      setItemTitleMap({});

      const testId    = test?.id;
      const patientId = member?.patient?.id || member?.patientId;

      if (!testId || !patientId) {
        setError("Missing test or patient information.");
        return;
      }

      const base = import.meta.env.VITE_API_BASE_URL;
      const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };

      const [resultRes, itemsRes] = await Promise.all([
        fetch(`${base}/api/results/find?orderId=${orderId}&testId=${testId}&patientId=${patientId}`, auth),
        fetch(`${base}/api/report-items/list/${testId}`, auth).catch(() => null),
      ]);

      const data = await resultRes.json();
      if (!resultRes.ok || !data?.success) throw new Error(data?.message || "Failed to load result");
      setResult(data?.data || null);

      if (itemsRes && itemsRes.ok) {
        try {
          const itemsJson = await itemsRes.json();
          const items = itemsJson?.data || [];
          const map = {};
          items.forEach((it) => {
            if (it?.id != null && (it.type === "FREE_TEXT" || it.type === "RICH_TEXT") && it.title) {
              map[String(it.id)] = it.title;
            }
          });
          setItemTitleMap(map);
        } catch (e) {
          console.error("Failed to parse report items", e);
        }
      }
    } catch (err) {
      console.error("ResultViewModal fetch error:", err);
      setError(err?.message || "Failed to load result");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const paramResults = result?.parameterResults || [];
  const hasParams    = paramResults.length > 0;
  const hasFreeTexts = (() => {
    if (!result?.notes) return false;
    try {
      const p = JSON.parse(result.notes);
      return Object.values(p.__freeTexts || {}).some(v => v && v.replace(/<[^>]*>/g, "").trim());
    } catch { return false; }
  })();
  const hasHtml      = !!result?.reportHtml && !hasFreeTexts;
  const isPathology  = String(result?.test?.testType || test?.testType || "").toUpperCase() === "PATHOLOGY";
  const hasSignature = result?.leftSignature || result?.centerSignature || result?.rightSignature;

  const statusColor = {
    APPROVED:    "bg-green-100 text-green-700",
    REPORTED:    "bg-blue-100 text-blue-700",
    DRAFT:       "bg-amber-100 text-amber-700",
    UNDER_REVIEW:"bg-violet-100 text-violet-700",
  }[result?.status] || "bg-gray-100 text-gray-600";

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10">

        {/* ── Header ───────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
              {isPathology
                ? <Beaker className="w-4 h-4 text-blue-600" />
                : <Activity className="w-4 h-4 text-blue-600" />}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {result?.test?.name || test?.name || "Test Result"}
              </h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                  {result?.test?.testType || test?.testType || "—"}
                </span>
                {result?.status && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
                    {result.status}
                  </span>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 transition-colors"
            type="button">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Patient + meta strip ─────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 px-6 py-2.5 bg-blue-50/50 border-b border-blue-100 text-xs text-gray-600 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <User className="w-3.5 h-3.5 text-blue-500" />
            <span className="font-medium text-gray-800">
              {result?.patient?.fullName || member?.patient?.fullName || "—"}
            </span>
          </div>
          {member?.patient?.contactNo && (
            <div className="flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-blue-400" />
              <span>{member.patient.contactNo}</span>
            </div>
          )}
          {result?.reportedAt && (
            <span className="ml-auto text-gray-400 text-[10px]">
              Reported: {new Date(result.reportedAt).toLocaleDateString("en-IN", {
                day: "numeric", month: "short", year: "numeric",
                hour: "2-digit", minute: "2-digit",
              })}
            </span>
          )}
        </div>

        {/* ── Body ─────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-7 h-7 text-blue-500 animate-spin" />
              <p className="text-sm text-gray-400">Loading result...</p>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-sm font-medium text-red-600">Could not load result</p>
              <p className="text-xs text-red-400">{error}</p>
              <button onClick={fetchResult}
                className="mt-1 px-4 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50" type="button">
                Retry
              </button>
            </div>
          )}

          {!loading && !error && result && (
            <div className="p-6 space-y-5">

              {/* ── reportHtml (primary content) ── */}
              {hasHtml && (
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <FileText className="w-3 h-3" /> Report
                    </span>
                  </div>
                  <div
                    className="px-6 py-5 text-sm text-gray-800 leading-relaxed prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: result.reportHtml }}
                  />
                </div>
              )}

              {/* ── parameterResults table (pathology) ── */}
              {hasParams && (
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <Beaker className="w-3 h-3" /> Parameters
                    </span>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50/60 border-b border-gray-100">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Parameter</th>
                        <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Result</th>
                        <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Unit</th>
                        <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Reference Range</th>
                        <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Flag</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {paramResults.map((pr, i) => {
                        const isAbnormal = pr.flag && !["N","NORMAL",""].includes(String(pr.flag).toUpperCase());
                        return (
                          <tr key={pr.id || i} className={isAbnormal ? "bg-red-50/40" : "hover:bg-gray-50/50"}>
                            <td className="px-4 py-3 font-medium text-gray-800">
                              {pr.parameter?.name || `Parameter ${i + 1}`}
                              {pr.parameter?.method && (
                                <span className="block text-[9px] text-gray-400 font-normal mt-0.5">{pr.parameter.method}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-semibold ${isAbnormal ? "text-red-600" : "text-gray-900"}`}>
                                {pr.valueText ?? (pr.valueNumber != null ? pr.valueNumber : "—")}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center text-gray-500">
                              {pr.unit || pr.parameter?.unit || "—"}
                            </td>
                            <td className="px-4 py-3 text-center text-gray-500">
                              {pr.normalRangeText || pr.parameter?.ranges?.[0]?.referenceRange || "—"}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <FlagBadge flag={pr.flag} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

             
              {/* Notes + Free Texts / Other Texts */}
              {result.notes && (() => {
                let plainNotes = result.notes;
                let freeTexts = {};
                try {
                  const parsed = JSON.parse(result.notes);
                  plainNotes = parsed.__notes || "";
                  freeTexts = parsed.__freeTexts || {};
                } catch { /* plain string */ }
                const freeTextEntries = Object.entries(freeTexts).filter(
                  ([, v]) => v && v.replace(/<[^>]*>/g, "").trim()
                );
                if (!plainNotes && !freeTextEntries.length) return null;
                return (
                  <div className="space-y-2">
                    {freeTextEntries.map(([itemId, html]) => {
                      const title = itemTitleMap[itemId];
                      return (
                        <div
                          key={itemId}
                          className="rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900 overflow-hidden"
                        >
                          <div className="flex items-stretch">
                            {title && (
                              <div className="w-1/3 min-w-[140px] px-3 py-2 bg-blue-100/70 border-r border-blue-200 text-[11px] font-semibold text-blue-900 flex items-center">
                                {title}
                              </div>
                            )}
                            <div
                              className="flex-1 p-3 prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: html }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {plainNotes && (
                      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: plainNotes }} />
                    )}
                  </div>
                );
              })()}

              {/* ── Signatures ── */}
              {hasSignature && (
                <div className="pt-4 mt-2 border-t border-gray-200">
                  <div className="flex items-end justify-between gap-4">
                    {/* Left */}
                    <SignatureCard sig={result.leftSignature} align="LEFT" />
                    {/* Center */}
                    <SignatureCard sig={result.centerSignature} align="CENTER" />
                    {/* Right */}
                    <SignatureCard sig={result.rightSignature} align="RIGHT" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ──────────────────────────────────────────── */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end flex-shrink-0">
          <button onClick={onClose}
            className="px-5 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            type="button">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   RESULT CHECK HELPERS
───────────────────────────────────────────────────────────────── */
const normalizeStatus = (s) => String(s || "").toUpperCase();

const isTestResultDone = (testObj, itemObj) => {
  if (itemObj?.resultAdded === true) return true;
  const st = testObj?.result?.status;
  if (!st) return false;
  const s = normalizeStatus(st);
  if (s === "DRAFT" || s === "PENDING") return false;
  return true;
};

const buildResultsSummary = (members) => {
  let pending = 0;
  let total = 0;
  (Array.isArray(members) ? members : []).forEach((m) => {
    const items = Array.isArray(m?.orderMemberPackages) ? m.orderMemberPackages : [];
    items.filter(Boolean).forEach((item) => {
      if (item?.test) {
        total += 1;
        if (!isTestResultDone(item.test, item)) pending += 1;
      }
      const cps = Array.isArray(item?.package?.checkupPackages) ? item.package.checkupPackages : [];
      cps.filter(Boolean).forEach((cp) => {
        if (!cp?.test) return;
        total += 1;
        if (!isTestResultDone(cp.test, cp)) pending += 1;
      });
    });
  });
  return { totalCount: total, pendingCount: pending, allResultsDone: total > 0 && pending === 0 };
};

/* ─────────────────────────────────────────────────────────────────
   ACTION BUTTONS — View + Edit/Enter
───────────────────────────────────────────────────────────────── */
const ActionButtons = ({ completed, onView, onEdit, onDownload }) => (
  <div className="flex items-center gap-1.5 justify-center">
    {/* View — only when result exists */}
    {completed && (
      <button
        onClick={onView}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 transition-colors"
        type="button"
        title="View Result">
        <Eye className="w-3 h-3" />
        View
      </button>
    )}

    {completed && onDownload && (
      <button
        onClick={onDownload}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 transition-colors"
        type="button"
        title="Download PDF">
        <Download className="w-3 h-3" />
        PDF
      </button>
    )}

    {/* Edit / Enter Result */}
    <button
      onClick={onEdit}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium text-white transition-colors ${
        completed
          ? "bg-green-600 hover:bg-green-700"
          : "bg-blue-600 hover:bg-blue-700"
      }`}
      type="button">
      {completed ? (
        <><Edit className="w-3 h-3" /> Edit</>
      ) : (
        <><PlusCircle className="w-3 h-3" /> Enter Result</>
      )}
    </button>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */
const TestsTab = ({ order, orderId, formatCurrency, onResultsSummary }) => {
  const [orderMembers, setOrderMembers] = useState([]);
  const [loading, setLoading]           = useState(true);

  const [selectedTest, setSelectedTest]             = useState(null);
  const [showPathologyModal, setShowPathologyModal] = useState(false);
  const [showRadiologyModal, setShowRadiologyModal] = useState(false);

  // ── NEW: view result modal state ──
  const [viewModal, setViewModal] = useState({
    open: false,
    test: null,
    member: null,
  });

  const [pdfModalOpen, setPdfModalOpen]             = useState(false);
  const [pdfSelectedPatientId, setPdfSelectedPatientId] = useState(null);

  const [testPdfModalOpen, setTestPdfModalOpen] = useState(false);
  const [testPdfMeta, setTestPdfMeta] = useState(null);

  const openPatientPdfModal = (patientId) => {
    if (!patientId) { alert("Patient not found"); return; }
    setPdfSelectedPatientId(patientId);
    setPdfModalOpen(true);
  };

  const openTestPdfModal = (test, member) => {
    const patientId = member?.patient?.id || member?.patientId;
    const testResultId = test?.result?.id;
    if (!patientId || !testResultId) {
      alert("Missing patient or result data for test-wise PDF");
      return;
    }
    setTestPdfMeta({ orderId, patientId, testResultId });
    setTestPdfModalOpen(true);
  };

  const fetchTests = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/orders/${orderId}/tests`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      const data = await res.json();
      if (data?.success) {
        const members = Array.isArray(data.orderMembers) ? data.orderMembers : [];
        setOrderMembers(members);
        onResultsSummary?.(buildResultsSummary(members));
      } else {
        setOrderMembers([]);
        onResultsSummary?.({ allResultsDone: false, pendingCount: 0, totalCount: 0 });
      }
    } catch (err) {
      console.error("Failed to fetch order tests", err);
      setOrderMembers([]);
      onResultsSummary?.({ allResultsDone: false, pendingCount: 0, totalCount: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId) fetchTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const downloadPatientPdfByUrl = async ({ orderId, patientId, type }) => {
    try {
      const url = `${import.meta.env.VITE_API_BASE_URL}/api/results/find-result?orderId=${orderId}&patientId=${patientId}&type=${type}`;
      const res  = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || "Failed");
      if (!data?.data?.url) throw new Error("Report URL missing");
      window.open(data.data.url, "_blank");
    } catch (err) {
      alert(err?.message || "Failed");
    }
  };

  const downloadTestPdf = (variant) => {
    if (!testPdfMeta) return;
    const { orderId, patientId, testResultId } = testPdfMeta;
    const url = `${import.meta.env.VITE_API_BASE_URL}/api/orders/${orderId}/${patientId}/test-pdf/${testResultId}?variant=${variant}`;
    window.open(url, "_blank");
    setTestPdfModalOpen(false);
    setTestPdfMeta(null);
  };

  const calculateMemberTotal = (member) => {
    const items = Array.isArray(member?.orderMemberPackages) ? member.orderMemberPackages : [];
    return items.reduce((total, item) => {
      if (!item) return total;
      // Use custom price if set, otherwise fall back to offer/actual price
      if (item.price != null) return total + Number(item.price);
      if (item?.package) return total + (Number(item.package.offerPrice || item.package.actualPrice) || 0);
      if (item?.test) return total + (Number(item.test.offerPrice || item.test.actualPrice) || 0);
      return total;
    }, 0);
  };

  // Open EDIT modal (pathology or radiology)
  const handleResultClick = (test, member, item) => {
    setSelectedTest({
      ...test,
      memberId: member?.id,
      memberName: member?.patient?.fullName,
      orderMemberPackageId: item?.id,
      existingResult: test?.result || null,
    });
    const type = String(test?.testType || "").toUpperCase();
    if (type === "PATHOLOGY") setShowPathologyModal(true);
    else setShowRadiologyModal(true);
  };

  // Open VIEW modal
  const handleViewClick = (test, member) => {
    setViewModal({ open: true, test, member });
  };

  if (loading) {
    return <div className="p-6 text-center text-sm text-gray-500">Loading tests...</div>;
  }

  if (!Array.isArray(orderMembers) || !orderMembers.length) {
    return (
      <div className="bg-white border rounded-lg p-6 text-center text-sm text-gray-500">
        No tests found for this order
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {orderMembers.filter(Boolean).map((member, i) => {
        // Use order.finalAmount for single-member orders (more accurate with discounts/custom prices)
        const memberTotal = orderMembers.length === 1 && order?.finalAmount != null
          ? order.finalAmount
          : calculateMemberTotal(member);
        const safeItems = (Array.isArray(member?.orderMemberPackages) ? member.orderMemberPackages : []).filter(Boolean);

        return (
          <div key={member?.id || `member-${i}`} className="border border-gray-200 rounded-lg bg-white shadow-sm">

            {/* ── MEMBER HEADER ── */}
            <div className="flex justify-between items-center px-5 py-3 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-600" />
                <div>
                  <h4 className="font-semibold text-gray-900">
                    {member?.patient?.fullName || `Patient ${i + 1}`}
                    {(member?.patient?.gender || member?.patient?.age || member?.patient?.dob) && (
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        ({[member?.patient?.gender, formatPatientAge(member?.patient)].filter(Boolean).join(", ")})
                      </span>
                    )}
                  </h4>
                  {member?.patient?.contactNo && (
                    <p className="text-xs text-gray-500">
                      <Phone className="inline w-3 h-3 mr-1" />{member.patient.contactNo}
                    </p>
                  )}
                </div>
              </div>
              <div className="text-right flex items-center gap-3">
                <div>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    <ClipboardList className="inline w-3 h-3 mr-1" />{safeItems.length} Items
                  </span>
                  <p className="text-lg font-bold mt-1">{formatCurrency(memberTotal)}</p>
                </div>
                {order?.reportReady && (
                  <button
                    onClick={() => openPatientPdfModal(member?.patientId)}
                    className="px-3 py-2 rounded-lg text-white text-xs bg-indigo-600 hover:bg-indigo-700"
                    type="button">
                    <Download className="inline w-4 h-4 mr-1" />Report
                  </button>
                )}
              </div>
            </div>

            {/* ── TABLE ── */}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Details</th>
                    <th className="p-3 text-center">Result</th>
                    <th className="p-3 text-center">Report Status</th>
                    <th className="p-3 text-right">Price</th>
                    <th className="p-3 text-center">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y">
                  {safeItems.map((item, idx) => {

                    /* ── PACKAGE ── */
                    if (item?.package) {
                      const pkg = item.package;
                      const cps = (Array.isArray(pkg?.checkupPackages) ? pkg.checkupPackages : []).filter(Boolean);

                      return (
                        <React.Fragment key={`pkg-${item?.id || idx}`}>
                          <tr className="bg-blue-50/40">
                            <td className="p-2">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-semibold">
                                <Package className="w-3 h-3" />Package
                              </span>
                            </td>
                            <td className="p-2 font-semibold text-gray-900">{pkg?.name || "—"}</td>
                            <td className="p-2 text-[11px] text-gray-600">Includes {pkg?.totalTests ?? cps.length ?? 0} tests</td>
                            <td className="p-2 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                pkg?.resultStatus === "COMPLETED" ? "bg-green-100 text-green-800" :
                                pkg?.resultStatus === "PARTIAL"   ? "bg-amber-100 text-amber-800" :
                                "bg-gray-100 text-gray-600"}`}>
                                {pkg?.resultStatus || "PENDING"}
                              </span>
                            </td>
                            <td className="p-2 text-center text-gray-400 text-xs">—</td>
                            <td className="p-2 text-right font-semibold">
                              {formatCurrency(item?.price ?? pkg?.offerPrice ?? pkg?.actualPrice ?? 0)}
                            </td>
                            <td className="p-2 text-center text-gray-400 text-xs">—</td>
                          </tr>

                          {cps.map((cp, cidx) => {
                            if (!cp?.test) return null;
                            const completed = isTestResultDone(cp.test, cp);
                            return (
                              <tr key={`pkg-test-${cp?.test?.id || `${idx}-${cidx}`}`} className="hover:bg-gray-50">
                                <td className="p-2 pl-7 text-[11px] text-gray-500">Test</td>
                                <td className="p-2 font-medium text-gray-900">{cp.test?.name || "—"}</td>
                                <td className="p-2 text-gray-600 text-[11px]">{cp.test?.testType || "—"}</td>
                                <td className="p-2 text-center">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${completed ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                                    {completed ? <><CheckCircle className="w-3 h-3" /> Done</> : <><Clock className="w-3 h-3" /> Pending</>}
                                  </span>
                                </td>
                                <td className="p-2 text-center"><StatusBadge status={cp.test?.result?.status} /></td>
                                <td className="p-2 text-right text-gray-400 text-xs">—</td>
                                <td className="p-2">
                                  <ActionButtons
                                    completed={completed}
                                    onView={() => handleViewClick(cp.test, member)}
                                    onEdit={() => handleResultClick(cp.test, member, item)}
                                    onDownload={() => openTestPdfModal(cp.test, member)}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    }

                    /* ── INDIVIDUAL TEST ── */
                    if (item?.test) {
                      const test      = item.test;
                      const completed = isTestResultDone(test, item);
                      return (
                        <tr key={`test-${item?.id || idx}`} className="hover:bg-gray-50">
                          <td className="p-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-800 text-[10px] font-semibold">
                              <FlaskConical className="w-3 h-3" />Test
                            </span>
                          </td>
                          <td className="p-2 font-medium text-gray-900">{test?.name || "—"}</td>
                          <td className="p-2 text-gray-600 text-[11px]">{test?.testType || "—"}</td>
                          <td className="p-2 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${completed ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                              {completed ? <><CheckCircle className="w-3 h-3" /> Done</> : <><Clock className="w-3 h-3" /> Pending</>}
                            </span>
                          </td>
                          <td className="p-2 text-center"><StatusBadge status={test?.result?.status} /></td>
                          <td className="p-2 text-right font-semibold">
                            {formatCurrency(item?.price ?? test?.offerPrice ?? test?.actualPrice ?? 0)}
                          </td>
                          <td className="p-2">
                            <ActionButtons
                              completed={completed}
                              onView={() => handleViewClick(test, member)}
                              onEdit={() => handleResultClick(test, member, item)}
                              onDownload={() => openTestPdfModal(test, member)}
                            />
                          </td>
                        </tr>
                      );
                    }

                    return null;
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* ── RESULT VIEW MODAL (NEW) ── */}
      <ResultViewModal
        open={viewModal.open}
        test={viewModal.test}
        member={viewModal.member}
        orderId={orderId}
        onClose={() => setViewModal({ open: false, test: null, member: null })}
      />

      {/* ── PDF MODAL ── */}
      <PdfChoiceModal
        open={pdfModalOpen}
        onClose={() => { setPdfModalOpen(false); setPdfSelectedPatientId(null); }}
        onPick={(type) => {
          const patientId = pdfSelectedPatientId;
          setPdfModalOpen(false);
          setPdfSelectedPatientId(null);
          downloadPatientPdfByUrl({ orderId, patientId, type });
        }}
      />

      <PdfChoiceModal
        title="Download Single Test PDF"
        open={testPdfModalOpen}
        onClose={() => { setTestPdfModalOpen(false); setTestPdfMeta(null); }}
        options={[
          { key: "plain", title: "Plain", desc: "Report only (no letterhead)" },
          { key: "letterhead", title: "With Letterhead", desc: "Includes clinic header" }
        ]}
        onPick={downloadTestPdf}
      />

      {/* ── RESULT ENTRY MODALS ── */}
      {selectedTest && (
        <>
          <PathologyResultModal
            isOpen={showPathologyModal}
            test={selectedTest}
            patient={orderMembers.find((m) => m?.id === selectedTest.memberId)?.patient}
            order={order}
            onClose={() => { setShowPathologyModal(false); setSelectedTest(null); }}
            onSave={() => { fetchTests(); setShowPathologyModal(false); setSelectedTest(null); }}
          />
          <RadiologyResultModal
            isOpen={showRadiologyModal}
            test={selectedTest}
            patient={orderMembers.find((m) => m?.id === selectedTest.memberId)?.patient}
            order={order}
            onClose={() => { setShowRadiologyModal(false); setSelectedTest(null); }}
            onSave={() => { fetchTests(); setShowRadiologyModal(false); setSelectedTest(null); }}
          />
        </>
      )}
    </div>
  );
};

export default TestsTab;